import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@satyrsmc/shared/components";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CategoryCombobox } from "@/components/ui/category-combobox";
import type { LineItem } from "@satyrsmc/shared/client";

interface NewLineItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: string[];
  onAddCategory: (name: string) => void;
  onCreate: (item: Omit<LineItem, "id">) => Promise<void>;
}

/**
 * Form modal for creating a budget line item. Collects all fields up front so the line can be
 * abandoned with no change — nothing is persisted until Save, avoiding the half-saved "New Item"
 * row and the inline-save race conditions of editing directly in the table.
 */
export function NewLineItemModal({
  open,
  onOpenChange,
  categories,
  onAddCategory,
  onCreate,
}: NewLineItemModalProps) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState(categories[0] ?? "");
  const [unitCost, setUnitCost] = useState("0");
  const [quantity, setQuantity] = useState("1");
  const [comments, setComments] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset to a clean form each time the modal opens so abandoned input never leaks back in.
  useEffect(() => {
    if (open) {
      setName("");
      setCategory(categories[0] ?? "");
      setUnitCost("0");
      setQuantity("1");
      setComments("");
      setSaving(false);
    }
  }, [open, categories]);

  const trimmedName = name.trim();
  const canSave = trimmedName.length > 0 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    const trimmedCategory = category.trim();
    setSaving(true);
    try {
      // A typed-but-new category needs to be registered before the item references it.
      if (trimmedCategory && !categories.includes(trimmedCategory)) {
        onAddCategory(trimmedCategory);
      }
      const parsedCost = parseFloat(unitCost);
      const parsedQty = parseFloat(quantity);
      await onCreate({
        name: trimmedName,
        category: trimmedCategory,
        unitCost: Number.isNaN(parsedCost) ? 0 : parsedCost,
        quantity: Number.isNaN(parsedQty) ? 0 : parsedQty,
        comments,
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New Line Item</DialogTitle>
          <DialogDescription>
            Fill in the details for the new line item. Nothing is saved until you add it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-line-item-name">Name</Label>
            <Input
              id="new-line-item-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Item name"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="new-line-item-cost">Unit Cost</Label>
              <Input
                id="new-line-item-cost"
                type="number"
                min={0}
                step={0.01}
                value={unitCost}
                onChange={(e) => setUnitCost(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-line-item-qty">Quantity</Label>
              <Input
                id="new-line-item-qty"
                type="number"
                min={0}
                step={1}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-line-item-category">Category</Label>
            <CategoryCombobox
              id="new-line-item-category"
              options={categories}
              value={category}
              onChange={setCategory}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-line-item-comments">Comments</Label>
            <Textarea
              id="new-line-item-comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Optional notes"
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {saving ? "Adding..." : "Add line item"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
